import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { notifyApartmentLogoChanged } from "../services/apartmentLogo";

const getLogoId = (row) => row?.logoId || row?._id || row?.id || "";

const getLogoUrl = (row) =>
  String(
    row?.logoViewUrl ||
      row?.logoUrl ||
      row?.imageUrl ||
      row?.url ||
      row?.image ||
      ""
  ).trim();

const getLogoStatus = (row) => {
  if (typeof row?.isActive === "boolean") return row.isActive ? "active" : "inactive";
  return String(row?.status || "").trim().toLowerCase() || "active";
};

const pickList = (data) => {
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.logos)) return data.logos;
  if (Array.isArray(data.appartmentLogos)) return data.appartmentLogos;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.result)) return data.result;
  if (Array.isArray(data.list)) return data.list;
  return [];
};

const pickOne = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.logo && typeof data.logo === "object") return data.logo;
  if (data.appartmentLogo && typeof data.appartmentLogo === "object") {
    return data.appartmentLogo;
  }
  if (data.result && typeof data.result === "object" && !Array.isArray(data.result)) {
    return data.result;
  }
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) {
    return data.data;
  }
  if (getLogoId(data) || getLogoUrl(data)) return data;
  return null;
};

const formatDateTime = (iso) => {
  if (iso == null || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const statusBadgeStyle = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "active") {
    return { background: "rgba(34,197,94,0.18)", color: "#16a34a" };
  }
  if (s === "inactive") {
    return { background: "rgba(239,68,68,0.18)", color: "#dc2626" };
  }
  return { background: "rgba(59,130,246,0.15)", color: "#2563eb" };
};

const Logo = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userId = useMemo(() => localStorage.getItem("userid") || "", []);

  const [items, setItems] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("view"); // view | edit
  const [selected, setSelected] = useState(null);
  const [editStatus, setEditStatus] = useState("active");
  const [loadingItem, setLoadingItem] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchList = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    let didIncrement = false;
    try {
      const payload = {
        action: "appartmentLogo.getAll",
        userId,
        page: 1,
        limit: 200,
        includeInactive: true,
        includeViewUrls: true,
        expiresIn: 3600
      };

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const list = pickList(data);
        setItems(Array.isArray(list) ? list : []);
      } else {
        toast.error(data?.message || "Failed to load logos");
        setItems([]);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
      setItems([]);
    } finally {
      if (didIncrement) loaderDecrement();
    }
  }, [API_BASE_URL, userId]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const closeModal = () => {
    setModalOpen(false);
    setSelected(null);
    setLoadingItem(false);
    setSaving(false);
  };

  const openLogoModal = async (mode, row) => {
    const logoId = getLogoId(row);
    if (!logoId) {
      toast.error("Missing logo id");
      return;
    }
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userId) {
      toast.error("Missing userId. Please login again.");
      return;
    }

    setModalMode(mode);
    setModalOpen(true);
    setLoadingItem(true);
    setSelected(null);

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "appartmentLogo.getById",
        userId,
        logoId,
        expiresIn: 3600
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        const item = pickOne(data);
        if (!item) {
          toast.error(data?.message || "Logo not found");
          closeModal();
          return;
        }
        setSelected(item);
        setEditStatus(getLogoStatus(item));
      } else {
        toast.error(data?.message || "Failed to load logo");
        closeModal();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
      closeModal();
    } finally {
      if (didIncrement) loaderDecrement();
      setLoadingItem(false);
    }
  };

  const handleUpdate = async () => {
    const logoId = getLogoId(selected);
    if (!logoId) {
      toast.error("Missing logo id");
      return;
    }
    if (!editStatus) {
      toast.error("Status is required");
      return;
    }

    setSaving(true);
    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "appartmentLogo.update",
        userId,
        logoId,
        status: editStatus,
        expiresIn: 3600
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Logo updated successfully");
        notifyApartmentLogoChanged();
        closeModal();
        fetchList();
      } else {
        toast.error(data?.message || "Failed to update logo");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
    } finally {
      if (didIncrement) loaderDecrement();
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const logoId = getLogoId(row);
    if (!logoId) {
      toast.error("Missing logo id");
      return;
    }
    if (!window.confirm("Deactivate this logo? (soft delete → inactive)")) {
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "appartmentLogo.delete",
        userId,
        logoId
      });
      const data = response?.data;

      if (data?.status === "success" || data?.success === true) {
        toast.success(data?.message || "Logo deactivated");
        notifyApartmentLogoChanged();
        fetchList();
      } else {
        toast.error(data?.message || "Failed to delete logo");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Something went wrong"
      );
    } finally {
      if (didIncrement) loaderDecrement();
    }
  };

  const rows = Array.isArray(items) ? items : [];
  const modalTitle = modalMode === "edit" ? "Edit Logo" : "View Logo";
  const selectedUrl = getLogoUrl(selected);
  const selectedStatus = getLogoStatus(selected);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div
          className="container-fluid d-flex justify-content-between align-items-end flex-wrap"
          style={{ gap: 12 }}
        >
          <div>
            <h1 className="m-0 txt_lft">Apartment Logo</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Manage apartment logos.
            </p>
          </div>
          <NavLink to="/logo-create" className="btn btn-primary">
            Add Logo
          </NavLink>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          <div className="card">
            <div className="card-body">
              <div className="role-table-box">
                <div
                  className="role-table-top d-flex flex-wrap align-items-center justify-content-between"
                  style={{ gap: 10 }}
                >
                  <div className="text-muted" style={{ fontSize: 13 }}>
                    Total: {rows.length}
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover role-table">
                    <thead>
                      <tr>
                        <th style={{ width: 70 }}>
                          <span className="role-th">Sl No.</span>
                        </th>
                        <th style={{ width: 100 }}>
                          <span className="role-th">Logo</span>
                        </th>
                        <th style={{ width: 120 }}>
                          <span className="role-th">Status</span>
                        </th>
                        <th>
                          <span className="role-th">Created</span>
                        </th>
                        <th>
                          <span className="role-th">Updated</span>
                        </th>
                        <th style={{ width: 150 }}>
                          <span className="role-th">Action</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length ? (
                        rows.map((row, idx) => {
                          const id = getLogoId(row);
                          const url = getLogoUrl(row);
                          const status = getLogoStatus(row);

                          return (
                            <tr key={String(id || `row-${idx}`)}>
                              <td>{idx + 1}</td>
                              <td>
                                {url ? (
                                  <img
                                    src={url}
                                    alt="Logo"
                                    style={{
                                      width: 48,
                                      height: 48,
                                      objectFit: "contain",
                                      borderRadius: 8,
                                      border: "1px solid #e8eef0",
                                      background: "#fff"
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  "—"
                                )}
                              </td>
                              <td>
                                <span
                                  className="badge"
                                  style={{
                                    padding: "0.35rem 0.65rem",
                                    borderRadius: 999,
                                    fontWeight: 600,
                                    fontSize: 11,
                                    ...statusBadgeStyle(status)
                                  }}
                                >
                                  {String(status || "—").toUpperCase()}
                                </span>
                              </td>
                              <td>{formatDateTime(row?.createdAt)}</td>
                              <td>{formatDateTime(row?.updatedAt)}</td>
                              <td>
                                <div className="d-flex" style={{ gap: 8 }}>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-view"
                                    title="View"
                                    onClick={() => openLogoModal("view", row)}
                                  >
                                    <i className="fa-solid fa-eye" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-edit"
                                    title="Edit"
                                    onClick={() => openLogoModal("edit", row)}
                                  >
                                    <i
                                      className="fa-solid fa-pen-to-square"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <button
                                    type="button"
                                    className="role-action-btn role-action-delete"
                                    title="Delete"
                                    onClick={() => handleDelete(row)}
                                  >
                                    <i className="fa-solid fa-trash" aria-hidden="true" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="text-center text-muted">
                            No logos found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16
          }}
          onClick={closeModal}
        >
          <div
            className="card app-modal-card"
            style={{ width: "min(480px, 96vw)", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header app-modal-header">
              <div className="app-modal-title">{modalTitle}</div>
              <button
                type="button"
                className="app-modal-close"
                onClick={closeModal}
                disabled={saving}
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div className="card-body">
              {loadingItem ? (
                <div className="text-center text-muted" style={{ padding: 24 }}>
                  Loading...
                </div>
              ) : selected ? (
                <>
                  <div className="text-center mb-3">
                    {selectedUrl ? (
                      <img
                        src={selectedUrl}
                        alt="Apartment logo"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 180,
                          objectFit: "contain",
                          borderRadius: 12,
                          border: "1px solid #e8eef0",
                          background: "#fff",
                          padding: 8
                        }}
                      />
                    ) : (
                      <div className="text-muted">No logo image</div>
                    )}
                  </div>

                  {modalMode === "view" ? (
                    <>
                      <div className="mb-2">
                        <span className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
                          Status
                        </span>
                        <div>
                          <span
                            className="badge"
                            style={{
                              padding: "0.35rem 0.65rem",
                              borderRadius: 999,
                              fontWeight: 600,
                              fontSize: 11,
                              ...statusBadgeStyle(selectedStatus)
                            }}
                          >
                            {String(selectedStatus || "—").toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
                          Created
                        </span>
                        <div style={{ fontSize: 14 }}>{formatDateTime(selected?.createdAt)}</div>
                      </div>
                      <div className="mb-0">
                        <span className="text-muted" style={{ fontSize: 12, fontWeight: 600 }}>
                          Updated
                        </span>
                        <div style={{ fontSize: 14 }}>{formatDateTime(selected?.updatedAt)}</div>
                      </div>
                    </>
                  ) : (
                    <div className="form-group mb-0">
                      <label className="form-label">
                        Status <span className="required-star">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        disabled={saving}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-muted">Logo not found</div>
              )}
            </div>

            <div className="card-footer d-flex justify-content-end" style={{ gap: 10 }}>
              {modalMode === "view" ? (
                <>
                  <button type="button" className="btn btn-secondary" onClick={closeModal}>
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setModalMode("edit")}
                    disabled={loadingItem || !selected}
                  >
                    Edit
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleUpdate}
                    disabled={saving || loadingItem}
                  >
                    {saving ? "Saving..." : "Save"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Logo;
